import React, { useEffect, useRef, useState } from 'react';
import { LOGO_IMAGES_V1_50, LOGO_IMAGES_V1_500, LOGO_IMAGES_V2 } from 'types';

import { ASComponentSize } from '@eleventheye/asui';
import useViewportDimensions from 'hooks/useViewportDimensions';
import styles from './TeamLogo.module.scss';

type TeamLogoProps = {
  className?: string;
  /** team name as a three letter abbreviation, it is used to determine `teamName.webp` image */
  teamName: string;
  isHomeTeam?: boolean;
  showHomeAwayText?: boolean;
  showTeamNameLabel?: boolean;
  onTeamClicked?: (team: string) => void;
  iconType?: 'v1' | 'v2';
  size?: ASComponentSize;
};

const TeamLogo: React.FC<TeamLogoProps> = ({
  className = '',
  teamName = '',
  isHomeTeam = false,
  showHomeAwayText = false,
  showTeamNameLabel = false,
  iconType = 'v1',
  onTeamClicked = () => undefined,
  size = '',
}) => {
  const imageRef = useRef<HTMLImageElement>(null);
  const baseCls = `${styles.TeamLogoContainer} ${size} ${className}`;
  const [dimensionCls, setDimensionCls] = useState(styles.TeamLogoBaseSize);
  const [showImageCls, setShowImageCls] = useState('');
  const [selectedTeamName, setSelectedTeamName] = useState(teamName);
  const { isAppMobile } = useViewportDimensions();

  const homeAwayLabel = isHomeTeam ? 'HOME' : 'AWAY';
  const altText = isHomeTeam ? `Home team ${selectedTeamName} logo` : `Away team ${selectedTeamName} logo`;

  const onImageLoaded = () => setShowImageCls(styles.ShowTeamLogo);

  useEffect(() => {
    if (imageRef.current) {
      imageRef.current.onload = () => onImageLoaded();

      if (imageRef.current.complete) {
        onImageLoaded();
      }
    }
    setDimensionCls(styles.TeamLogoBaseSize);
  }, []);

  useEffect(() => {
    if (!teamName) return;
    setSelectedTeamName(teamName);
  }, [teamName]);

  const v1_images = isAppMobile ? LOGO_IMAGES_V1_50 : LOGO_IMAGES_V1_500;
  const scrPrefix = iconType === 'v1' ? v1_images : LOGO_IMAGES_V2;

  return (
    <div className={`${baseCls} ${dimensionCls}`}>
      {showHomeAwayText && <span>{homeAwayLabel}</span>}
      <img
        ref={imageRef}
        src={`${scrPrefix}${selectedTeamName.toLowerCase()}.webp`}
        alt={altText}
        data-teamname={selectedTeamName}
        onClick={() => onTeamClicked(selectedTeamName)}
        className={`${styles.TeamLogoImg} ${showImageCls} ${selectedTeamName === 'empty' ? styles.Empty : ''}`}
      />
      {showTeamNameLabel && <span>{selectedTeamName}</span>}
    </div>
  );
};

export default TeamLogo;
